/**
 * Conversation Transformer
 *
 * The backend formatter (transformConversationForFrontend) now sends camelCase
 * ConversationResponse objects. This transformer maps them to the frontend
 * ConversationData shape, which is mostly a pass-through with a few additions.
 */

import { BackendConversation, ConversationData } from '../types';

/**
 * Transform backend conversation (formatter output) to frontend ConversationData format
 */
export const transformConversation = (conv: BackendConversation): ConversationData => {
  // Extract contact info from participants for fields the backend doesn't provide
  const contactParticipant = conv.participants?.find(p => p.contactId && p.contact);
  const contact = contactParticipant?.contact;
  const contactIdParticipant = contactParticipant || conv.participants?.find(p => p.contactId);

  const senderName = conv.senderName;

  return {
    // Direct pass-through from backend formatter output
    id: conv.id,
    title: conv.title || 'Unknown Conversation',
    phoneNumber: conv.phoneNumber,
    contactId: conv.contactId || contactIdParticipant?.contactId || undefined,
    lastMessage: conv.lastMessage || '',
    timestamp: conv.timestamp,
    unreadCount: conv.unreadCount || 0,
    participants: conv.participants || [],
    assignees: conv.assignees || [],
    isContact: conv.isContact,
    contactAvatarColor: conv.contactAvatarColor,
    contactProfileImageUrl: conv.contactProfileImageUrl,
    avatarColor: conv.avatarColor,
    contactFirstName: conv.contactFirstName,
    contactLastName: conv.contactLastName,
    initials: conv.initials,
    conversationType: conv.conversationType,
    isOpen: conv.isOpen,
    avatarText: conv.avatarText,
    isMuted: conv.isMuted,
    tags: conv.tags || [],
    otherContacts: conv.otherContacts || [],

    // HawkSoft client linkage
    hawksoftClientId: conv.hawksoftClientId || null,

    // Sender information
    senderName,
    senderProfileImageUrl: conv.senderProfileImageUrl || null,
    senderAvatarColor: conv.senderAvatarColor || null,

    // Additional fields derived from participant data
    email: contact?.email ?? undefined,
    type: conv.conversationType as 'client' | 'direct' | 'group',
    contact: contact ? {
      id: contact.id,
      phoneNumber: contact.phoneNumber ?? undefined,
      avatarColor: contact.avatarColor ?? undefined,
      email: contact.email ?? undefined,
    } : undefined,
  };
};

/**
 * Transform an array of backend conversations
 */
export const transformConversations = (backendConversations: BackendConversation[]): ConversationData[] => {
  if (!Array.isArray(backendConversations)) {
    return [];
  }
  return backendConversations.map(transformConversation);
};
